import {Box, HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState, useSyncExternalStore} from 'react';

import {isNil} from 'ramda';

import {type TestIdProps, useOnMount} from 'shared/web';

import type {EntityResourceResolver} from '../../context/EntityResourceResolver';
import {useBulkOperationCancellation} from '../../hooks/useBulkOperationCancellation';
import {useBulkOperationRecovery} from '../../hooks/useBulkOperationRecovery';
import {useBulkOperationTracking} from '../../hooks/useBulkOperationTracking';
import type {
  BulkAction,
  DataGridBulkActionsResponseBody,
  SubmitFilteredRecordsBulkOperationRequestBody,
  SubmitBulkOperationResponseBody,
} from '../../types/api/bulkOperations';
import type {
  BulkOperationActionCallback,
  BulkOperationSelectionSource,
} from '../../types/BulkOperationActionCallback';
import type {BulkOperationActionDefinitions} from '../../types/BulkOperationActionDefinition';
import type {BulkOperationsServices} from '../../types/BulkOperationsServices';
import {getAvailableBulkOperationTargets} from '../../utils/getAvailableBulkOperationTargets';
import {isBulkOperationSupportedByGrid} from '../../utils/isBulkOperationSupportedByGrid';
import {BulkOperationDialog} from '../BulkOperationDialog/BulkOperationDialog';
import {GridPagination, type PaginationProps} from '../Pagination/GridPagination';
import {ResultSummary} from '../ResultSummary/ResultSummary';
import {BulkOperationActionsToolbar} from './BulkOperationActionsToolbar';
import {BulkOperationSelectionSummary} from './BulkOperationSelectionSummary';
import {BulkOperationsPanel} from './BulkOperationsPanel';
import {useBulkOperationActionDispatch} from './hooks/useBulkOperationActionDispatch';
import {useBulkOperationLink} from './hooks/useBulkOperationLink';
import {InvokeBulkOperationAction} from './InvokeBulkOperationAction';
import {serializeBulkOperationLink} from './utils/bulkOperationLink';
import {
  createBulkOperationDismissals,
  type BulkOperationDismissals,
} from './utils/createBulkOperationDismissals';

type BulkOperationDialogState = {type: 'operation'; bulkOperationId: string} | null;

type BulkOperationsProps = TestIdProps & {
  actionsDefinition: BulkOperationActionDefinitions;
  discovery: DataGridBulkActionsResponseBody | undefined;
  gridCode: string;
  presetInstanceId?: string;
  services: BulkOperationsServices;
  actionCallback?: BulkOperationActionCallback;
  selectionSource: BulkOperationSelectionSource;
  entityResourceResolver?: EntityResourceResolver;
  pagination?: PaginationProps;
  onRefresh: () => void;
  submitFiltered: (
    body: SubmitFilteredRecordsBulkOperationRequestBody
  ) => Promise<SubmitBulkOperationResponseBody>;
  onFilteredSelectionChange?: (selected: boolean) => void;
  pageRecordIds?: string[];
  onSelectionChange?: (recordIds: string[]) => void;
};

type ScopeLinkedBulkOperationProps = {
  bulkOperationId: string;
  gridCode: string;
  presetInstanceId?: string;
  onScope: (value: string) => void;
};

function ScopeLinkedBulkOperation(props: ScopeLinkedBulkOperationProps) {
  useOnMount(() =>
    props.onScope(
      serializeBulkOperationLink(props.gridCode, props.presetInstanceId, props.bulkOperationId)
    )
  );
  return null;
}

export function BulkOperations(props: BulkOperationsProps) {
  // Panel visibility and dismissals live above the key boundary to survive grid/preset switches.
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [dismissals] = useState(() => createBulkOperationDismissals());
  return (
    <BulkOperationsContent
      key={JSON.stringify([props.gridCode, props.presetInstanceId])}
      {...props}
      isPanelOpen={isPanelOpen}
      onPanelOpenChange={setIsPanelOpen}
      dismissals={dismissals}
    />
  );
}

function BulkOperationsContent(
  props: BulkOperationsProps & {
    isPanelOpen: boolean;
    onPanelOpenChange: (isOpen: boolean) => void;
    dismissals: BulkOperationDismissals;
  }
) {
  const [hasShownPanel, setHasShownPanel] = useState(false);
  const dismissed = useSyncExternalStore(props.dismissals.subscribe, props.dismissals.getSnapshot);
  const selection = useSyncExternalStore(
    props.selectionSource.subscribe,
    props.selectionSource.getSnapshot
  );
  const link = useBulkOperationLink(props.gridCode, props.presetInstanceId);
  const discovery = props.discovery;
  // Completion notifications carry only the operation ID. Resolve that operation first, then
  // replace the URL value with its grid owner before any mounted grid renders the dialog.
  const unscopedLookupId =
    discovery === undefined ? undefined : (link.unscopedBulkOperationId ?? undefined);
  const tracking = useBulkOperationTracking({
    services: props.services,
    discovery,
    linkedOperationId: link.ownedBulkOperationId ?? unscopedLookupId,
  });
  const unscopedBulkOperation = tracking.linked.operation;
  const visibleOperations = tracking.operations.filter((operation) =>
    isNil(dismissed[operation.bulkOperationId])
  );
  if (visibleOperations.length > 0 && !hasShownPanel) {
    setHasShownPanel(true);
  }
  const shouldScopeLinkedOperation =
    link.unscopedBulkOperationId !== null &&
    unscopedBulkOperation !== undefined &&
    unscopedBulkOperation.bulkOperationId === link.unscopedBulkOperationId &&
    discovery !== undefined &&
    isBulkOperationSupportedByGrid(unscopedBulkOperation, discovery);
  const dialogState: BulkOperationDialogState =
    link.ownedBulkOperationId !== null
      ? {type: 'operation', bulkOperationId: link.ownedBulkOperationId}
      : null;

  const recovery = useBulkOperationRecovery({
    services: props.services,
    onRegistered: (operation, sourceOperationId) => {
      tracking.register(operation);
      if (link.ownedBulkOperationId === sourceOperationId) {
        link.clear();
      }
    },
  });

  const cancellation = useBulkOperationCancellation({
    services: props.services,
    onSettled: tracking.refresh,
  });

  const actionDispatch = useBulkOperationActionDispatch({
    discovery,
    actionsDefinition: props.actionsDefinition,
    actionCallback: props.actionCallback,
    selectionSource: props.selectionSource,
    services: props.services,
    gridCode: props.gridCode,
    presetInstanceId: props.presetInstanceId,
    submitFiltered: props.submitFiltered,
    onOpen: () => {
      if (link.value !== null) {
        link.clear();
      }
    },
    onSubmitted: (operation) => {
      tracking.register(operation);
      props.onSelectionChange?.([]);
    },
    testId: props['data-testid'],
  });

  const handleCloseOperationDialog = () => {
    link.clear();
    props.onRefresh();
  };

  const handleTryAgain = (actionCode: string) => {
    const action = discovery?.actions.find((candidate) => candidate.actionCode === actionCode);
    link.clear();
    if (action !== undefined) {
      actionDispatch.openSubmitDialog(action);
    }
  };

  const handleOpenAction = (action: BulkAction) => actionDispatch.openSubmitDialog(action);

  const dialog = match(dialogState)
    .with({type: 'operation'}, ({bulkOperationId}) => (
      <BulkOperationDialog
        services={props.services}
        entityResourceResolver={props.entityResourceResolver}
        bulkOperationId={bulkOperationId}
        tracking={tracking.linked}
        recovery={recovery}
        cancellation={cancellation}
        actionsDefinition={props.actionsDefinition}
        onClose={handleCloseOperationDialog}
        onOperationChange={tracking.register}
        onTryAgain={handleTryAgain}
        data-testid={props['data-testid']}
      />
    ))
    .with(null, () => null)
    .exhaustive();

  const actions = discovery?.actions ?? [];
  const targets = getAvailableBulkOperationTargets(selection);
  const isSelectionActive =
    selection.recordIds.length > 0 || selection.allFilteredSelected === true;
  // Concurrent operations are supported; only a missing callback or empty selection disables.
  const areActionsDisabled =
    props.actionCallback === undefined || (targets.filtered === null && targets.selected === null);

  const controls = (
    <Show when={actions.length > 0}>
      <Box flexGrow={1}>
        <HStack spacing={3} align="center" wrap>
          <BulkOperationSelectionSummary
            presentation={props.services.presentation}
            selection={selection}
            resourceId={discovery?.resourceId}
            isSelectionActive={isSelectionActive}
            hasVisibleOperation={visibleOperations.length > 0}
            pageRecordIds={props.pageRecordIds}
            onSelectionChange={props.onSelectionChange}
            onFilteredSelectionChange={props.onFilteredSelectionChange}
          />
          <Show
            when={
              visibleOperations.length > 0 ||
              (hasShownPanel && (tracking.history.isLoading || tracking.history.isError))
            }
          >
            <BulkOperationsPanel
              operations={visibleOperations}
              history={tracking.history}
              actionsDefinition={props.actionsDefinition}
              services={props.services}
              isOpen={props.isPanelOpen}
              onOpen={() => props.onPanelOpenChange(true)}
              onClose={() => props.onPanelOpenChange(false)}
              onViewDetail={(bulkOperationId) => {
                props.onPanelOpenChange(false);
                link.open(bulkOperationId);
              }}
              recovery={recovery}
              cancellation={cancellation}
              onDismiss={props.dismissals.dismiss}
              data-testid={props['data-testid']}
            />
          </Show>
          <BulkOperationActionsToolbar
            actions={actions}
            actionsDefinition={props.actionsDefinition}
            isDisabled={areActionsDisabled}
            onOpenAction={handleOpenAction}
            data-testid={props['data-testid']}
          />
        </HStack>
      </Box>
    </Show>
  );

  return (
    <>
      <Show when={actionDispatch.pendingAction}>
        {(action) => <InvokeBulkOperationAction key={action.id} invoke={action.invoke} />}
      </Show>
      <Show when={shouldScopeLinkedOperation ? link.unscopedBulkOperationId : null}>
        {(bulkOperationId) => (
          <ScopeLinkedBulkOperation
            bulkOperationId={bulkOperationId}
            gridCode={props.gridCode}
            presetInstanceId={props.presetInstanceId}
            onScope={link.replace}
          />
        )}
      </Show>
      <Show when={actions.length > 0 || props.pagination !== undefined}>
        <div
          data-eag-grid-footer
          className={`flex items-center gap-3 flex-wrap px-4 py-2.5 border-t relative z-(--z-header-popper) ${isSelectionActive ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'}`}
        >
          <Show when={props.pagination}>
            {(pagination) => <ResultSummary {...pagination} variant="range" />}
          </Show>
          <Show when={props.pagination !== undefined && actions.length > 0}>
            <span aria-hidden className="h-7 w-px shrink-0 bg-blue-200" />
          </Show>
          {controls}
          <Show
            when={
              props.pagination !== undefined &&
              props.pagination.totalPages > 1 &&
              actions.length > 0
            }
          >
            <span aria-hidden className="h-7 w-px shrink-0 bg-blue-200" />
          </Show>
          <Show when={props.pagination}>
            {(pagination) => <GridPagination {...pagination} navigationOnly />}
          </Show>
        </div>
      </Show>
      <Show when={actions.length > 0}>{dialog}</Show>
    </>
  );
}
